import { useRef } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import type { Project } from '@frameio/graphql-client/generated';
import { getLink } from '@frameio/shared/routing/getLink';
import { formatBytes } from '@frameio/utils/formatters';
import {
  Box,
  ProjectAvatar,
  Text,
  theme,
  useEmulateButton,
} from '@frameio/vapor';
import {
  ListItemDetailRow,
  MediaContainer,
} from '../../../../QuickSearchListItem/components';
import { interactiveStyles } from '../../../../shared';
import TEXT from './generated/TEXT/ProjectCard';

const MEDIA_CONTAINER_WIDTH = 100;
const MEDIA_CONTAINER_HEIGHT = 56;

TEXT.DEFINE({
  /** Label indicating this is a project  */
  project: 'Project',
});

type RecentlyViewedProjectProps = Pick<
  Project,
  'id' | 'name' | 'imageUrl' | 'storage'
> & {
  workspaceName: string;
  onClick: (name: string, href: string) => void;
};

export const ProjectCard = ({
  id,
  name,
  imageUrl,
  workspaceName,
  storage,
  onClick,
}: RecentlyViewedProjectProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { bind } = useEmulateButton(ref, 'link');
  const href = getLink({
    page: 'folder',
    projectId: id,
  });

  return (
    <Link href={href} onClick={() => onClick(name, href)} tabIndex={-1}>
      <ProjectCardContainer ref={ref} {...bind}>
        <MediaContainer
          height={MEDIA_CONTAINER_HEIGHT}
          width={MEDIA_CONTAINER_WIDTH}
        >
          <ProjectAvatar
            id={id}
            size={40}
            src={imageUrl ?? ''}
            title={name ?? ''}
          />
        </MediaContainer>
        <DetailsContainer display="flex" flexDirection="column" gap={4}>
          <Text truncate={true}>{name}</Text>
          <ListItemDetailRow
            leftSegment={
              <Text color="tertiary" truncate={true} variant="caption">
                {TEXT.project}
              </Text>
            }
            middleSegment={
              <Text color="tertiary" variant="caption">
                {formatBytes(storage)}
              </Text>
            }
            rightSegment={
              <Text
                color="tertiary"
                enableTooltip={true}
                truncate={true}
                variant="caption"
              >
                {workspaceName}
              </Text>
            }
          />
        </DetailsContainer>
      </ProjectCardContainer>
    </Link>
  );
};

const ProjectCardContainer = styled.div`
  ${interactiveStyles}
  align-items: center;
  display: flex;
  gap: ${theme.spaces[12]};
  padding: ${theme.spaces[8]};
  transition: background-color ${theme.transitions.durations['150']}
    ${theme.transitions.eases.primary};

  &:hover {
    background-color: ${theme.colors.background.interactive.hover};
  }
`;

const DetailsContainer = styled(Box)`
  flex: 1;
  min-width: 0;
`;
